import { Mark, mergeAttributes } from "@tiptap/core";

/**
 * Font-size mark — wraps the selected text in a span carrying both
 * a `data-size` attribute (the LaTeX size token) and an inline
 * `font-size` style scaled relative to the doc's current base size.
 *
 * Per Claude Design 2026-05-09 (Q37): the chip and the live editor
 * preview must render the size the doc will actually produce. We
 * store the LaTeX token (`Large`, `huge`, etc.) and let the LaTeX
 * exporter wrap selections in `{\<token> ... }` on render. The CSS
 * font-size is the visual fidelity layer; LaTeX is the truth layer.
 *
 * Standard 10 LaTeX sizes (no extra package needed):
 *
 *   tiny .50× · scriptsize .70× · footnotesize .80× · small .90×
 *   normalsize 1.00× · large 1.20× · Large 1.44× · LARGE 1.73×
 *   huge 2.07× · Huge 2.49×
 *
 * Ratios are doc-base-relative; the chip-render side multiplies by
 * the user's `class_options.fontSize` (10/11/12pt). This mark only
 * stores the token — the CSS scale is computed at render.
 */

export const SIZE_RATIOS: Record<string, number> = {
  tiny: 0.5,
  scriptsize: 0.7,
  footnotesize: 0.8,
  small: 0.9,
  normalsize: 1.0,
  large: 1.2,
  Large: 1.44,
  LARGE: 1.728,
  huge: 2.074,
  Huge: 2.488,
};

export type FontSizeToken = keyof typeof SIZE_RATIOS;

export const FontSize = Mark.create({
  name: "fontSize",

  addAttributes() {
    return {
      size: {
        default: "normalsize",
        parseHTML: (el) => el.getAttribute("data-size") || "normalsize",
        renderHTML: (attrs) => {
          if (!attrs.size || attrs.size === "normalsize") return {};
          const ratio = SIZE_RATIOS[attrs.size as string] ?? 1.0;
          return {
            "data-size": attrs.size,
            style: `font-size: ${ratio}em`,
          };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-size]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes), 0];
  },
});
